/**
 * Editor empty-state — the empty-state hero, workspace toggle, and reset path.
 *
 * Extracted from editor.ts in Phase EB.1 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §7 — Cluster B).
 *
 * Functions:
 *   - syncTopBarPadding — recomputes body padding so the top-bar stack doesn't
 *     overlap the editor toolbar (re-fires on resize + when the env-banner
 *     injects async via ResizeObserver).
 *   - showEmptyState — reveals the drop-target hero and hides workspace
 *     controls (zoom, page-nav, attestation pill, reset).
 *   - showWorkspace — flips visibility once a PDF is loaded; takes
 *     `applyLayout` as a callback because pane-layout lives in editor.ts.
 *   - resetEditorState — the coordinator-style teardown that nullifies
 *     state.* fields, destroys per-page controllers, clears the bbox cache,
 *     and transitions the lifecycle SM to EMPTY_WARM.
 *
 * Side effects on import:
 *   - `window.addEventListener("resize", syncTopBarPadding)`
 *   - `requestAnimationFrame(syncTopBarPadding)` (initial call)
 *   - ResizeObserver on #top-bar-stack that re-fires syncTopBarPadding when
 *     the async env-banner injection changes the stack height.
 *
 * Notes:
 *   - `showWorkspace(applyLayout)` takes the pane-layout `applyLayout` as a
 *     parameter rather than importing pane-layout directly. The _paneLayout
 *     singleton lives in editor.ts (orchestrator scope) — see design doc §9
 *     update in this phase's commit message for the cross-cluster review.
 *   - `editPaneBody` is looked up locally rather than promoted to a shared
 *     state field, because it's only used by Cluster B (this module) and
 *     Cluster F (card-pane). Cluster F still does its own `$()` lookup at
 *     line ~2047 of editor.ts — they remain independent lookups for now.
 */
import { state, els, _bboxCache } from "./editor-state.js";
import { dispatch, replaceIR } from "./dispatcher.js";
import { recordOpApplied } from "./save-flow.js";
import { clearOpenFile } from "./session-store.js";
import { SaveBtnState, setSaveBtnState } from "./save-btn-state.js";
import { EditorLifecycleState } from "./editor-lifecycle-state.js";
// Phase FB.5 (frontend-mv-architecture-260524.md): PendingInsertStore
// relocated to store/. The view-layer instantiates a fresh store on
// reset; that's "view→store" but actually invocation of a store's
// constructor (a write to module-state via the export-installed class)
// is permitted under the store layer's "store mutations happen via
// dispatched actions" framing — here, "construct a fresh store" is the
// reset action. (The wider views→store violations stay flagged until
// the action-dispatch refactor; this import does not increase that
// count.)
import { PendingInsertStore } from "./store/pending-insert.js";
import { log } from "./log.js";
import { dispatchUpload } from "./upload-routing.js";
import { renderEmptyStateContent } from "./editor-empty-state-content.js";
import { registerTransientOdpJob } from "./editor-transient.js";
import { announce } from "../a11y-view/announce.js";
// Local shorthand for optional DOM lookups (returns `HTMLElement | null`).
// Mirrors editor.ts's `$` shorthand; intentionally duplicated rather than
// imported because dom-utils.ts exports the throw-on-missing variants only.
const _$ = (id) => document.getElementById(id); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
// ─── Top-bar continuity shim ───────────────────────────────────────────────
export function syncTopBarPadding() {
    const stack = _$("top-bar-stack");
    if (!stack)
        return;
    const h = stack.offsetHeight;
    document.body.style.paddingTop = h + "px"; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
}
window.addEventListener("resize", syncTopBarPadding); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
requestAnimationFrame(syncTopBarPadding);
// WHY: env-banner.js asynchronously injects a STAGING/LOCAL banner into
// #env-banner-slot AFTER fetch('/api/auth/me') resolves. The initial
// requestAnimationFrame(syncTopBarPadding) above runs at boot; it does NOT
// fire again when the banner is injected, so the body paddingTop stays at
// the pre-banner height and the editor toolbar renders ~28px too high —
// partly under the STAGING banner. ResizeObserver re-fires
// syncTopBarPadding whenever #top-bar-stack's height changes, fixing the
// overlap without a redundant polling loop.
(function observeTopBarStackHeight() {
    const stack = _$("top-bar-stack");
    if (!stack)
        return;
    if (typeof ResizeObserver === "undefined")
        return;
    const ro = new ResizeObserver(() => syncTopBarPadding());
    ro.observe(stack);
    // Best-effort teardown on page unload — keeps a long-lived debug
    // session from accumulating observers if the editor is reloaded
    // in-place by a future hot-reload story.
    window.addEventListener("pagehide", () => {
        try {
            ro.disconnect();
        }
        catch { /* best-effort cleanup */ }
    }, { once: true });
})();
// ─── Empty-state content — build-time-dynamic from the routing SSOT ────────
//
// Project EDITOR_ROUTE_BY_EXT onto the hero's icon row + lead-in + facts list
// at boot (bug2-editor-emptystate-dynamic-from-ssot). The static HTML carries
// only empty containers (#editor-empty-iconrow / #editor-empty-sub-lead /
// #editor-empty-facts); this fills them, so `.odp` (and any future editor
// format) appears with no HTML edit. Runs once at import — the DOM is parsed by
// the time this ES-module executes (els.* getRequired lookups above already
// depend on it).
renderEmptyStateContent();
// ─── Empty-state show / hide ─────────────────────────────────────────────
export function showEmptyState() {
    els.emptyState.classList.remove("hidden");
    els.workspace.classList.add("hidden");
    els.workspace.setAttribute("aria-hidden", "true");
    els.fileName.textContent = "No file open";
    setSaveBtnState(els.saveBtn, SaveBtnState.EMPTY);
    // Download is only meaningful once a document is loaded.
    els.downloadBtn.disabled = true;
    els.undoBtn.disabled = true;
    els.redoBtn.disabled = true;
    // Hide controls that only make sense once a PDF is open.
    if (els.zoomControls)
        els.zoomControls.style.display = "none";
    if (els.pageNav)
        els.pageNav.style.display = "none";
    if (els.attestationPill)
        els.attestationPill.style.display = "none";
    if (els.resetBtn)
        els.resetBtn.style.display = "none";
    // Clear-tagging is only meaningful once a document is open. Optional lookup
    // (not a required `els` entry) so partial test DOM scaffolds don't break.
    const clearTaggingBtn = _$("editor-clear-tagging-btn");
    if (clearTaggingBtn)
        clearTaggingBtn.style.display = "none";
    // Export-as-ground-truth needs a loaded document's IR — same show/hide as
    // clear-tagging (its authoring sibling).
    const exportGtBtn = _$("editor-export-gt-btn");
    if (exportGtBtn)
        exportGtBtn.style.display = "none";
}
/**
 * Reveal the workspace once a PDF is loaded.
 *
 * Takes `applyLayout` as a callback because the pane-layout singleton lives
 * in editor.ts (orchestrator scope). The orchestrator passes its local
 * `applyLayout` reference at every call site.
 */
export function showWorkspace(applyLayout) {
    // Hide the empty-state hero entirely once a PDF is loaded.
    els.emptyState.classList.add("hidden");
    els.workspace.classList.remove("hidden");
    els.workspace.setAttribute("aria-hidden", "false");
    setSaveBtnState(els.saveBtn, SaveBtnState.READY);
    // Enable Download now that a document is loaded.
    els.downloadBtn.disabled = false;
    // Show PDF-active controls.
    if (els.zoomControls)
        els.zoomControls.style.display = "";
    if (els.pageNav)
        els.pageNav.style.display = "";
    if (els.attestationPill)
        els.attestationPill.style.display = "";
    if (els.resetBtn)
        els.resetBtn.style.display = "";
    const clearTaggingBtn = _$("editor-clear-tagging-btn");
    if (clearTaggingBtn)
        clearTaggingBtn.style.display = "";
    const exportGtBtn = _$("editor-export-gt-btn");
    if (exportGtBtn)
        exportGtBtn.style.display = "";
    // Re-apply layout so pane widths are correct for the new container size.
    applyLayout();
}
/**
 * Reset ONLY the logical-structure caches + their rendered surfaces, leaving the
 * loaded document (PDF, interop, jobId, page geometry) and lifecycle untouched.
 *
 * This is the structure-reset SUBSET that `resetEditorState` (full teardown →
 * EMPTY_WARM) and `clearAllTagging` (empty the tags, keep the PDF open) BOTH need
 * (rule #11 / A.6 — extract on the second site). `resetEditorState` calls it as one
 * step of its teardown; `clearAllTagging` calls it after emptying `state.ir` so the
 * card pane + overlay re-render against a blank structure.
 *
 * What it clears: the per-structure caches (`cards`, selection, expanded set,
 * `cardByStructId`, `readingOrderByStructId`, `structIdsWithLongDescNext`,
 * `blocksByPage`, `overlayRoleMap`) and their rendered DOM (the card list + the PDF
 * overlay boxes). What it does NOT touch: `pdfDoc`/`interop`/`jobId`, `attestation`,
 * viewport (`currentPage`/`currentScale`/`pageDescriptors`), controllers, the
 * lifecycle SM. Those are teardown-only concerns owned by `resetEditorState`.
 */
export function clearStructureState() {
    state.cards = [];
    state.selectedStructIds = new Set();
    state.expandedIds = new Set();
    state.cardByStructId = new Map();
    state.readingOrderByStructId = new Map();
    state.structIdsWithLongDescNext = new Set();
    state.blocksByPage.clear();
    state.overlayRoleMap = null;
    // Clear the card list DOM. Uses a local lookup rather than a shared ref —
    // editor.ts holds the canonical editPaneBody reference for the cards
    // pane; this site re-looks it up locally so the empty-state module
    // stays independent of Cluster F.
    const editPaneBody = _$("pane-editor-body");
    if (editPaneBody) {
        const cardListEl = editPaneBody.querySelector(".editor-card-list");
        if (cardListEl)
            cardListEl.innerHTML = "";
    }
    // Clear the PDF overlay boxes.
    if (els.overlay)
        els.overlay.innerHTML = "";
}
/**
 * Clear all tagging (#7) — empty the logical structure so the user hand-tags the
 * document from a blank slate, WITHOUT unloading the PDF. The page images, MCIDs,
 * zoom/scroll, and interop all stay; only the tags are emptied.
 *
 * The `clear-all-tags` reducer empties `blocks` while PRESERVING `pages` + `mcids`
 * (design G-3 correctness constraint): `create-element` anchors a new hand-authored
 * element's bbox to the still-present MCID/page geometry, so wiping those would make
 * hand-authoring from scratch impossible. Lifecycle intentionally stays `LOADED_FULL`
 * — this is "empty the tags," not "close the file" (`resetEditorState`).
 *
 * INV-OPLOG-1 — the clear MUST route through the oplog SOLE SEAM, not a bare
 * `replaceIR`. Emptying the client IR without producing an op leaves the backend
 * checkpoint fully tagged, so the download serves the stale still-tagged bytes
 * (the clear-tagging bug this Epic fixes). So this follows the §B.2 four-step
 * persist pattern: (1) `dispatch` the op (reducer empties blocks + notifies
 * subscribers); (2) push it to `state.opLog` (the persistence deque — this is
 * what makes the unsaved-check fire so download flushes first); (3) arm the
 * auto-save cadence via `recordOpApplied` (→ POST /api/editor-edit → backend
 * strips /StructTreeRoot). `clearStructureState()` then resets the per-structure
 * caches + DOM. See phase-1-design-260814.md §Phase 3.
 */
export function clearAllTagging() {
    if (!state.ir)
        return; // nothing loaded — no structure to clear.
    const op = { kind: "clear-all-tags" };
    // (1) dispatch — the reducer empties blocks (preserving pages/mcids, G-3),
    // commits state.ir, and notifies viewport + doc-level subscribers.
    dispatch(op);
    // (2) push to the persistence deque so `state.opLog.size() > 0` — the
    // unsaved-check (editor-modals-save.ts) now flushes before download.
    state.opLog.push(op);
    // (3) arm the auto-save cadence → POST /api/editor-edit applies the strip to
    // the backend checkpoint so GET /api/editor-download serves untagged bytes.
    void recordOpApplied({
        jobId: state.jobId ?? "",
        format: state.structTree?.format?.toLowerCase(),
        opLog: state.opLog,
        pendingInsertStore: state.pendingInsertStore,
    });
    clearStructureState();
    if (state.undoRedo)
        state.undoRedo.clear();
    announce("All tagging cleared. Hand-tag the document from a blank slate.");
}
/**
 * Reset editor state to the empty state WITHOUT a page reload.
 *
 * The WASM module (state.interop) is intentionally preserved so the next
 * file the user opens skips the ~8-10s cold-boot, loading in ~2s instead.
 *
 * WHY: used by:
 *   1. The "Close" toolbar button — lets the user discard the current file
 *      and start fresh without waiting for WASM to re-initialise.
 *   2. Playwright smoke tests — resets DOM/IR/undo state between test cases
 *      while keeping the browser context (and WASM cache) alive, which cuts
 *      per-test WASM cold-boot from ~8s to ~2s.
 *
 * What is cleared:
 *   - PDF document + struct tree + IR (state.pdfDoc / structTree / ir)
 *   - state.interop + state.jobId (the interop is a jobId-bound
 *     BackendEditorInterop; a fresh drop registers a new transient job and
 *     rebuilds the interop against it — Phase 4)
 *   - Card list DOM + state.cards
 *   - Selection + expanded-set
 *   - Undo history
 *   - Multi-page scroll manager (page frames are recreated on next load)
 *   - IDB / sessionStorage via clearOpenFile()
 *
 * What is preserved:
 *   - localStorage preferences (zoom, overlay toggle, pane fraction)
 *   - Playwright browser context (Cache Storage, service-worker reg)
 */
export function resetEditorState() {
    // Clear persisted file from IDB + sessionStorage (best-effort, async).
    // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget; resetEditorState is sync (callers rely on void return)
    clearOpenFile().catch((err) => {
        log.warn("[editor] clearOpenFile on reset failed:", err);
    });
    // Per-page bbox cache must be cleared before the next loadFile so a
    // returning user doesn't see overlays from the previous PDF. Predicate 3
    // of the lazy-load redesign (260508). NB: declaration is at module scope
    // so this also covers the path where resetEditorState() is called from
    // EMPTY_WARM.
    _bboxCache.clear();
    // Nullify per-file state.
    //
    // Since the WASM editor was deleted (pdf-editor-no-itext Phase 4), `state.interop`
    // is a jobId-bound `BackendEditorInterop`; a reset MUST clear it (and the jobId)
    // so the next drop registers a FRESH transient job and rebuilds the interop
    // against that jobId. Leaving the stale interop intact would point the next
    // document's edits at the previous job's checkpoint. (Pre-Phase-4 the interop
    // was the reusable WASM module and was intentionally preserved across reset.)
    state.interop = null;
    state.jobId = null;
    state.pdfDoc = null;
    state.structTree = null;
    // Phase FB.9 (260524): route the IR teardown through the dispatcher's
    // replaceIR seam so subscribers observe the transition (per
    // docs/design/frontend-mv-architecture-260524.md §P.5 — dispatch monopoly).
    // replaceIR clears state.blocksByPage as part of the commit; the explicit
    // clear below was redundant pre-FB.9 and is preserved as a defensive no-op
    // for readers who scan the teardown for "what gets reset."
    replaceIR(null, "teardown");
    state.attestation = null;
    // Reset the logical-structure caches + their rendered DOM (card list +
    // overlay). Shared with clearAllTagging (rule #11). blocksByPage was also
    // cleared by replaceIR above; clearStructureState re-clears it defensively.
    clearStructureState();
    state.currentPage = 1;
    state.currentScale = NaN;
    state.pageDescriptors = [];
    // Destroy the multi-page scroll manager so it doesn't reference stale frames.
    if (state.multiPageMgr && typeof state.multiPageMgr.destroy === "function") {
        try {
            state.multiPageMgr.destroy();
        }
        catch { /* best-effort */ }
    }
    state.multiPageMgr = null;
    state.pageCtxDetachers = new Map();
    state.dragMoveControllers = new Map();
    state.resizeMoveControllers = new Map();
    state.rubberBandControllers = new Map();
    state.drawCreateControllers = new Map();
    // Replace the PendingInsertStore so save-block subscribers are notified
    // via the new (empty) store rather than the old store's stale entries.
    state.pendingInsertStore = new PendingInsertStore();
    // (Card-list DOM + PDF overlay were cleared by clearStructureState above.)
    // Destroy the screen-reader panel (if mounted) to release its DOM nodes.
    if (state.srPanel) {
        try {
            state.srPanel.destroy();
        }
        catch { /* best-effort */ }
        state.srPanel = null;
    }
    // Detach linked-views so stale card/overlay references don't hold up GC.
    state.linkedViewsCtrl = null;
    // Detach keyboard + context-menu listeners wired in mountCardPane().
    if (state.multiSelectDetach) {
        try {
            state.multiSelectDetach();
        }
        catch { /* best-effort */ }
        state.multiSelectDetach = null;
    }
    if (state.contextMenuDetach) {
        try {
            state.contextMenuDetach();
        }
        catch { /* best-effort */ }
        state.contextMenuDetach = null;
    }
    // Reset undo history so cross-document op records don't surface.
    if (state.undoRedo)
        state.undoRedo.clear();
    // Lifecycle: any LOADED_* / LOADING state -> EMPTY_WARM. (Pre-Phase-4 this
    // preserved the reusable WASM interop; post-Phase-4 the interop is cleared
    // above and rebuilt per-file, so EMPTY_WARM is now just "chrome is booted,
    // no file open" — the transition table still admits loadFile from it.)
    // The tryTransition is intentional: calling resetEditorState() while already
    // in EMPTY_COLD / EMPTY_WARM is a legal no-op (e.g., the hero's "Cancel").
    // Generation bumps in EMPTY_WARM so any in-flight Tier-2 callback
    // detects the race and bails. window.__editorPdfReady is reset to
    // false via the on_enter hook.
    if (!state.lifecycle.isAny([
        EditorLifecycleState.EMPTY_COLD,
        EditorLifecycleState.EMPTY_WARM,
    ])) {
        state.lifecycle.transition(EditorLifecycleState.EMPTY_WARM);
    }
    // Return to the empty state.
    showEmptyState();
    announce("Editor reset. Open a PDF to begin.");
}
let _emptyStateLoadFile = null;
/** Inject the orchestrator-owned `loadFile` closure. */
export function installEmptyStateLoadFile(loadFile) {
    _emptyStateLoadFile = loadFile;
}
/**
 * Open a drag-drop / file-picker PDF. The transient-job registration that gives a
 * direct-upload entry its jobId (so the jobId-keyed backend editor plumbing serves
 * it — the WASM in-browser parser was deleted in pdf-editor-no-itext Phase 4) lives
 * inside `loadFile` itself, the single load chokepoint, so every entry point
 * (drag-drop, picker, ?url= auto-open, session restore) registers uniformly.
 */
function _openFile(file) {
    if (_emptyStateLoadFile)
        void _emptyStateLoadFile(file); // fire-and-forget: load path registers the transient job + surfaces its own toast
}
// Wire the Close/Reset button click.
if (els.resetBtn) {
    els.resetBtn.addEventListener("click", () => resetEditorState());
}
// Wire the Clear-tagging button click (optional element — see showWorkspace).
const _clearTaggingBtn = _$("editor-clear-tagging-btn");
if (_clearTaggingBtn) {
    _clearTaggingBtn.addEventListener("click", () => clearAllTagging());
}
els.browseLink.addEventListener("click", () => els.fileInput.click());
els.fileInput.addEventListener("change", (ev) => {
    const target = ev.target;
    const file = target.files?.[0];
    if (file)
        _routeUploadedFile(file); // type-dispatch by ext → PDF load / Office route / honest reject
    // Reset so the same file can be re-opened.
    target.value = "";
});
// "Open" button in toolbar triggers the file input.
els.openBtn.addEventListener("click", () => els.fileInput.click());
// Explicit JS navigation for the back link. (Historically this bypassed the
// Blazor <Router>'s document-level click interception; the Blazor-WASM bundle
// was deleted in pdf-editor-no-itext Phase 4, but an explicit location.assign()
// remains the simplest, dependency-free navigation and is kept as-is.)
const _backLink = document.querySelector(".editor-back-link"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
if (_backLink) {
    _backLink.addEventListener("click", (ev) => {
        ev.preventDefault();
        window.location.assign(_backLink.getAttribute("href") ?? "/");
    });
}
// WHY: the entire empty-state section accepts drops, not just the inner
// dashed card. Without this, a user who releases the drag a few px outside
// the card border falls onto the surrounding <main>, the browser's default
// "open file in tab" wins, and the editor surface is replaced by the raw
// PDF — looking exactly like "drop did nothing." The card retains the
// is-dragover visual cue (so the user sees the highlight where it lands),
// but the listener targets are widened to the section.
const _dropZones = [els.emptyState, els.emptyCard].filter(Boolean);
["dragenter", "dragover"].forEach((evt) => {
    _dropZones.forEach((zone) => {
        zone.addEventListener(evt, (ev) => {
            ev.preventDefault();
            els.emptyCard.classList.add("is-dragover");
        });
    });
});
["dragleave", "drop"].forEach((evt) => {
    _dropZones.forEach((zone) => {
        zone.addEventListener(evt, (ev) => {
            ev.preventDefault();
            els.emptyCard.classList.remove("is-dragover");
        });
    });
});
// ─── Unified upload type-dispatch (unified-accessibility-editor-260715 §3.1) ──
//
// The unified `/editor` entry accepts PDF/PPTX/DOCX/XLSX and ROUTES each format
// to its backend via the typed routing seam (upload-routing.ts). PDF registers a
// transient job (`POST /api/editor-transient`) and opens the WASM shell in
// place — struct-tree parsing + edits run on the backend `editor-ir`/
// `editor-edit` endpoints (pdf-editor-no-itext-in-browser-260714: there is no
// more in-browser iText parse; only the PDF.js page-image render stays local).
// Office (.pptx/.docx) has no direct editor backend here (the PDF-only
// `/api/editor-transient` rejects it), so a FRESH Office upload is routed to
// the main pipeline (`/`) to be remediated before `/o365-view` can render it —
// announced honestly, never a silent no-op (rule #17). XLSX spreadsheet
// editing HAS shipped (xlsx-a11y-editor-260813, `/o365-view?format=xlsx`) but
// only for an already-remediated file — this direct-upload entry still has no
// route for a FRESH `.xlsx` (tracked as a [FIX] follow-up: `directUploadAction`
// for `.xlsx` in upload-routing.ts is stale and arguably should become
// `pipeline-upload` like Office, now that the review surface exists). An
// unsupported extension surfaces an accessible error. Post-remediation the
// job-card "Editor" button (renderers.ts) opens the surface-correct review
// view via `entry.hrefFor(jobId)` — which DOES cover `.xlsx` today (the
// shared `editor-routing.ts` map, not this file's stale `hrefFor: null` row).
/** Human label for a route entry's surface (used in accessible messages). */
function _surfaceLabel(entry) {
    // The label lives in the routing SSOT (rule #11 — one source, no second
    // hardcoded per-ext switch to drift). Same field the empty-state hero renders.
    return entry.label;
}
/**
 * Register a fresh `.odp` as a passthrough editor job, then navigate to the
 * shared a11y editor keyed on the returned jobId. On failure, announce an
 * accessible error and stay on the empty state (there is no in-browser ODP
 * parser to degrade to). Fire-and-forget from the sync dispatch.
 */
async function _openOdpPassthrough(file, entry) {
    announce(`Opening "${file.name}" in the accessibility editor…`);
    try {
        const jobId = await registerTransientOdpJob(file);
        const href = entry.hrefFor(jobId);
        if (href === null) {
            // Unreachable: the .odp route always carries a non-null hrefFor.
            announce(`Couldn't open "${file.name}" — no editor view for this file.`);
            return;
        }
        window.location.assign(href); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    }
    catch (err) {
        log.warn("odp passthrough registration failed", err);
        announce(`Couldn't open "${file.name}" in the accessibility editor. `
            + `Please check the file is a valid OpenDocument Presentation and try again.`);
    }
}
/**
 * Route a fresh (jobId-less) file dropped/browsed onto the unified entry. Reads
 * the routing seam's `directUploadAction` — never a bespoke per-format `if`.
 */
function _routeUploadedFile(file) {
    const decision = dispatchUpload(file);
    if (decision.kind === "unsupported") {
        // rule #17: accessible, explicit — never a silent no-op.
        announce(`The Accessibility Editor supports PDF, PowerPoint, Word, and Excel files. `
            + `"${file.name}" isn't one of those.`);
        return;
    }
    const { entry } = decision;
    switch (entry.directUploadAction) {
        case "wasm-load":
            // PDF: register a transient job + load the WASM editor shell in place.
            void _openFile(file); // fire-and-forget: load path registers the transient job + surfaces its own toast
            return;
        case "odp-passthrough-load":
            // ODP: register a passthrough job (POST /api/editor-transient-odp),
            // then navigate to the shared a11y editor on the returned jobId. ODP
            // full remediation is Phase 4 (PILOT-gated); the passthrough ships the
            // editor now (libreoffice-odf Phase 5b/5d). The ODP editor is a
            // separate surface (/o365-view?format=odp), so we navigate rather than
            // load in place like PDF.
            void _openOdpPassthrough(file, entry); // fire-and-forget: surfaces its own announce on success/failure
            return;
        case "pipeline-upload":
            // Office: no direct Office backend on /editor — remediate via the main
            // pipeline first. Announce (aria-live) so the redirect isn't a surprise,
            // then navigate to the multi-format upload entry. WHY the short delay:
            // an immediate location.assign() tears the page down before the
            // aria-live region can announce, swallowing the message for a screen-
            // reader user — the delay lets the announcement land, then navigates.
            announce(`${_surfaceLabel(entry)} files are remediated through the main pipeline. `
                + `Opening the upload page.`);
            window.setTimeout(() => {
                window.location.assign("/"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            }, 600);
            return;
        case "reject-not-yet":
            // XLSX: spreadsheet editing HAS shipped (xlsx-a11y-editor-260813,
            // /o365-view?format=xlsx) — it just isn't reachable from THIS
            // direct-upload entry yet (no auto-redirect, unlike Office above;
            // see the [FIX] follow-up on the dispatch comment). Announce
            // truthfully: no "isn't available" claim, no implied redirect.
            announce(`Excel spreadsheet editing is available once "${file.name}" is `
                + `remediated. Upload it through the main pipeline, then open it `
                + `from your job list to edit alt text and other accessibility metadata.`);
            return;
    }
}
_dropZones.forEach((zone) => {
    zone.addEventListener("drop", (ev) => {
        const dragEv = ev;
        const file = dragEv.dataTransfer?.files?.[0];
        if (file)
            _routeUploadedFile(file);
    });
});
